import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { Plus, ArrowsLeftRight, Truck, XCircle, CheckCircle, Clock, X as XIcon, Trash, WarningCircle, Lightning, CalendarBlank, Package, PaperPlaneTilt, CaretUp, CaretDown, CaretUpDown, ArrowSquareOut, Star } from "@phosphor-icons/react";

const STATUS_STYLE = {
  aperto: "border-sky-400 text-sky-800 bg-sky-50",
  evaso: "border-emerald-500 text-emerald-800 bg-emerald-50",
  annullato: "border-neutral-400 text-neutral-700 bg-neutral-50",
};
// Labels shown as coloured pill. "aperto" from Flux = the physical ODT is on
// the warehouseman's plate → "Inviato a Magazzino". "aperto" from a manual
// entry stays "Aperto".
const statusLabel = (row) => {
  if (row.status === "aperto") return row.source === "flux" ? "Inviato a Magazzino" : "Aperto";
  if (row.status === "evaso") return "Evaso";
  return "Annullato";
};
const SOURCE_STYLE = {
  flux: "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5",
  manuale: "border-neutral-400 text-neutral-700 bg-neutral-50",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");
const fmtInterventionDate = (iso) => {
  if (!iso) return null;
  // Accepts either "YYYY-MM-DD" (Flux payload) or ISO timestamps.
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString("it-IT");
  } catch { return iso; }
};
// Extract Flux ticket code "TK-XXXX" from the note (Flux sends note like
// "Ticket: TK-0803 · Intervento: 2026-09-28").
// Legacy fallback: extract Flux ticket code "TK-XXXX" from the note.
// From 2026-06 Flux sends the code as a structured field ``ticket_code`` —
// this parser is only used when that field is missing (historical ODTs).
const parseTicket = (note) => {
  if (!note) return null;
  const m = String(note).match(/TK-\d+/i);
  return m ? m[0].toUpperCase() : null;
};

// Read the ticket code(s) off an ODT row.
// Preference order: structured `ticket_code` > first of `ticket_codes` > parsed
// from `note`. Returns { primary, extraCount, all } for compact display.
const readTicket = (r) => {
  const list = Array.isArray(r?.ticket_codes) ? r.ticket_codes.filter(Boolean) : [];
  const primary = r?.ticket_code || list[0] || parseTicket(r?.note) || null;
  const extraCount = list.length > 1 ? list.length - 1 : 0;
  return { primary, extraCount, all: list };
};

// Build the Flux ticket URL from the configured template, or null if the
// template is empty / doesn't contain the {ticket} placeholder.
const fluxTicketHref = (code, template) => {
  if (!code || !template || !template.includes("{ticket}")) return null;
  return template.replace("{ticket}", encodeURIComponent(code));
};

// Header cell that toggles a client-side sort on this column.
// Clicks cycle: none → asc → desc → none.
const SortableTh = ({ label, col, sort, onToggle, className = "" }) => {
  const active = sort?.col === col;
  const dir = active ? sort.dir : null;
  return (
    <th className={`px-4 py-3 text-left ${className}`}>
      <button
        type="button"
        onClick={() => onToggle(col)}
        data-testid={`odt-sort-${col}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider text-[11px] hover:text-[#002FA7] transition-colors ${active ? "text-[#002FA7]" : ""}`}
      >
        {label}
        {dir === "asc" && <CaretUp size={11} weight="bold" />}
        {dir === "desc" && <CaretDown size={11} weight="bold" />}
        {!dir && <CaretUpDown size={11} className="opacity-40" />}
      </button>
    </th>
  );
};

export default function TransferOrders() {
  const { user } = useAuth();
  const canFulfill = user?.role === "admin" || user?.role === "gestore";
  const canCreate = user?.role !== "operator"; // admin/gestore/viewer

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("aperto");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // Flux ticket URL template. Loaded from /api/integrations; empty string
  // disables the clickable badges (falls back to plain text).
  const [fluxTicketUrl, setFluxTicketUrl] = useState("");
  // Client-side per-column filters (case-insensitive substring match).
  const [fNumero, setFNumero] = useState("");
  const [fTecnico, setFTecnico] = useState("");
  const [fTicket, setFTicket] = useState("");
  const [fMateriali, setFMateriali] = useState("");
  // Sort state: null (natural = server default = most-recent-first) or
  // { col: 'numero'|'intervention'|'status', dir: 'asc'|'desc' }.
  const [sort, setSort] = useState(null);
  const toggleSort = (col) => setSort((s) => {
    if (!s || s.col !== col) return { col, dir: "asc" };
    if (s.dir === "asc") return { col, dir: "desc" };
    return null; // 3rd click clears sort
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const { data } = await api.get("/transfer-orders", { params });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  // Load the Flux ticket URL template once. Errors are silent — the badges
  // simply stay non-clickable if the endpoint is unreachable.
  useEffect(() => {
    api.get("/integrations")
      .then(({ data }) => setFluxTicketUrl(data?.flux_ticket_url_template || ""))
      .catch(() => {});
  }, []);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/transfer-orders/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Hard-delete an ODT (only available for non-evaso rows; the backend
  // enforces the same rule with a 409).
  const deleteRow = async (row) => {
    if (row.status === "evaso") {
      toast.error("Non puoi eliminare un ODT già evaso");
      return;
    }
    const label = row.flux_numero != null ? `ODT #${row.flux_numero}` : `ODT ${row.id.slice(0,8)}`;
    if (!window.confirm(`Eliminare ${label}? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/transfer-orders/${row.id}`);
      toast.success(`${label} eliminato`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Filter + sort pipeline (client-side).
  const visibleRows = useMemo(() => {
    const nq = fNumero.trim().toLowerCase();
    const tq = fTecnico.trim().toLowerCase();
    const kq = fTicket.trim().toLowerCase();
    const mq = fMateriali.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (nq) {
        const n = [
          r.flux_numero != null ? `#${r.flux_numero}` : "",
          r.odt_number || "",
        ].join(" ").toLowerCase();
        if (!n.includes(nq)) return false;
      }
      if (tq && !(r.technician_name || "").toLowerCase().includes(tq)) return false;
      if (kq) {
        const t = readTicket(r);
        const haystack = [t.primary, ...(t.all || [])].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(kq)) return false;
      }
      if (mq) {
        // Match against sku / name / supplier_name of ANY item on the ODT
        const materials = (r.items || [])
          .map((it) => [it.sku, it.name, it.supplier_name].filter(Boolean).join(" "))
          .join(" ")
          .toLowerCase();
        if (!materials.includes(mq)) return false;
      }
      return true;
    });
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      const pick = (r) => {
        if (sort.col === "numero") return r.flux_numero ?? (r.odt_number || "~");
        if (sort.col === "intervention") return r.intervention_date || "";
        if (sort.col === "status") return r.status || "";
        return 0;
      };
      out = [...out].sort((a, b) => {
        const va = pick(a), vb = pick(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return out;
  }, [rows, fNumero, fTecnico, fTicket, fMateriali, sort]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Logistica"
        title="Ordini di Trasferimento"
        subtitle="ODT ricevuti da Flux o creati manualmente per rifornire i furgoni dei tecnici"
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 text-xs">
          {[
            { v: "aperto", label: "Aperti" },
            { v: "evaso", label: "Evasi" },
            { v: "annullato", label: "Annullati" },
            { v: "", label: "Tutti" },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`odt-filter-${f.v || "all"}`}
              onClick={() => setStatus(f.v)}
              className={`px-3 py-1.5 border transition-colors ${status === f.v
                ? "bg-[#002FA7] text-white border-[#002FA7]"
                : "bg-white border-neutral-300 hover:bg-neutral-50"}`}
            >{f.label}</button>
          ))}
        </div>
        <div className="flex-1" />
        {canCreate && (
          <button
            type="button"
            data-testid="odt-new-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC]"
          >
            <Plus size={16} weight="bold" /> Nuovo ODT manuale
          </button>
        )}
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-neutral-500 text-sm">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">
            <ArrowsLeftRight size={30} className="mx-auto mb-2 opacity-40" />
            Nessun ordine di trasferimento.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 uppercase text-[11px] tracking-wider">
              <tr>
                <SortableTh label="N°" col="numero" sort={sort} onToggle={toggleSort} className="w-[80px]" />
                <SortableTh label="Intervento" col="intervention" sort={sort} onToggle={toggleSort} className="w-[110px]" />
                <th className="px-4 py-3 text-left">Tecnico</th>
                <th className="px-4 py-3 text-left w-[120px]">Ticket</th>
                <th className="px-4 py-3 text-left">Materiali</th>
                <SortableTh label="Stato" col="status" sort={sort} onToggle={toggleSort} className="w-[170px]" />
                <th className="px-4 py-3 text-center w-[70px]">Azioni</th>
              </tr>
              {/* Filter row */}
              <tr className="bg-white border-b border-neutral-200">
                <th className="px-2 py-2">
                  <input
                    type="text" value={fNumero} onChange={(e) => setFNumero(e.target.value)}
                    placeholder="Filtra…" data-testid="odt-filter-numero"
                    className="w-full px-2 py-1 border border-neutral-300 text-xs font-normal normal-case tracking-normal focus:outline-none focus:border-[#002FA7]"
                  />
                </th>
                <th></th>
                <th className="px-2 py-2">
                  <input
                    type="text" value={fTecnico} onChange={(e) => setFTecnico(e.target.value)}
                    placeholder="Filtra…" data-testid="odt-filter-tecnico"
                    className="w-full px-2 py-1 border border-neutral-300 text-xs font-normal normal-case tracking-normal focus:outline-none focus:border-[#002FA7]"
                  />
                </th>
                <th className="px-2 py-2">
                  <input
                    type="text" value={fTicket} onChange={(e) => setFTicket(e.target.value)}
                    placeholder="Filtra…" data-testid="odt-filter-ticket"
                    className="w-full px-2 py-1 border border-neutral-300 text-xs font-normal normal-case tracking-normal focus:outline-none focus:border-[#002FA7]"
                  />
                </th>
                <th className="px-2 py-2">
                  <input
                    type="text" value={fMateriali} onChange={(e) => setFMateriali(e.target.value)}
                    placeholder="SKU, prodotto o fornitore…" data-testid="odt-filter-materiali"
                    className="w-full px-2 py-1 border border-neutral-300 text-xs font-normal normal-case tracking-normal focus:outline-none focus:border-[#002FA7]"
                  />
                </th>
                <th></th><th></th>
              </tr>
            </thead>
            <tbody data-testid="odt-rows">
              {visibleRows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-xs text-neutral-500">
                  Nessun ODT corrisponde ai filtri.
                </td></tr>
              )}
              {visibleRows.map((r) => {
                const ticket = readTicket(r);
                const canDelete = r.status !== "evaso" && canFulfill;
                return (
                <tr key={r.id} data-testid={`odt-row-${r.id}`}
                    onClick={() => openDetail(r.id)}
                    className="border-b border-neutral-100 hover:bg-[#002FA7]/5 cursor-pointer align-top">
                  {/* N° */}
                  <td className="px-4 py-3 font-semibold text-[#002FA7]" data-testid={`odt-numero-${r.id}`}>
                    {r.flux_numero != null
                      ? `#${r.flux_numero}`
                      : r.odt_number
                        ? <span className="font-mono text-xs">{r.odt_number}</span>
                        : <span className="text-neutral-400 font-normal">—</span>}
                  </td>
                  {/* Intervento */}
                  <td className="px-4 py-3 text-neutral-700 whitespace-nowrap" data-testid={`odt-intervention-${r.id}`}>
                    {r.intervention_date
                      ? fmtDateShort(r.intervention_date)
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  {/* Tecnico */}
                  <td className="px-4 py-3 font-medium">{r.technician_name}</td>
                  {/* Ticket */}
                  <td className="px-4 py-3 font-mono text-xs text-neutral-600" data-testid={`odt-ticket-${r.id}`} onClick={(e) => e.stopPropagation()}>
                    {ticket.primary ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        {(() => {
                          const href = fluxTicketHref(ticket.primary, fluxTicketUrl);
                          return href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Apri ${ticket.primary} su Flux`}
                              data-testid={`odt-ticket-link-${r.id}`}
                              className="inline-flex items-center gap-0.5 text-[#002FA7] hover:underline"
                            >
                              {ticket.primary}
                              <ArrowSquareOut size={10} weight="bold" className="opacity-70" />
                            </a>
                          ) : (
                            <span>{ticket.primary}</span>
                          );
                        })()}
                        {ticket.extraCount > 0 && (
                          <span
                            title={ticket.all.join(", ")}
                            className="inline-flex items-center px-1 border border-neutral-300 text-[10px] text-neutral-600 bg-neutral-50 font-sans"
                          >+{ticket.extraCount}</span>
                        )}
                      </div>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  {/* Materiali preview */}
                  <td className="px-4 py-3" data-testid={`odt-materials-${r.id}`}>
                    <div className="space-y-1.5">
                      {(r.items || []).slice(0, 3).map((it) => (
                        <div key={it.product_id} className="flex items-start gap-2 text-xs">
                          <Package size={14} className="text-neutral-400 shrink-0 mt-0.5" />
                          <div className="flex-1 leading-snug">
                            <span className="text-neutral-800">{it.name}</span>
                            {it.supplier_name && (
                              <span className="text-neutral-500"> ({it.supplier_name})</span>
                            )}
                            <span className="text-neutral-500"> · <span className="font-mono">{it.sku}</span></span>
                          </div>
                          <span className="font-mono text-neutral-600 shrink-0 tabular-nums">×{it.quantity}</span>
                        </div>
                      ))}
                      {(r.items || []).length > 3 && (
                        <div className="text-[11px] text-neutral-500 pl-6">
                          e altri {r.items.length - 3}…
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Stato */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 border text-[11px] uppercase tracking-wider whitespace-nowrap ${STATUS_STYLE[r.status]}`}>
                      {r.status === "aperto" && r.source === "flux" && <PaperPlaneTilt size={11} weight="bold" />}
                      {r.status === "aperto" && r.source !== "flux" && <Clock size={11} weight="bold" />}
                      {r.status === "evaso" && <CheckCircle size={11} weight="bold" />}
                      {r.status === "annullato" && <XCircle size={11} weight="bold" />}
                      {statusLabel(r)}
                    </span>
                  </td>
                  {/* Azioni */}
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      disabled={!canDelete}
                      title={canDelete ? "Elimina ODT" : (r.status === "evaso" ? "ODT già evaso: non eliminabile" : "Permesso negato")}
                      data-testid={`odt-delete-${r.id}`}
                      onClick={() => deleteRow(r)}
                      className={`inline-flex items-center justify-center p-1.5 border transition-colors ${
                        canDelete
                          ? "border-red-300 text-red-600 hover:bg-red-50"
                          : "border-neutral-200 text-neutral-300 cursor-not-allowed"
                      }`}
                    >
                      <Trash size={15} weight="bold" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateODTModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {detail && (
        <ODTDetailModal
          odt={detail}
          canFulfill={canFulfill}
          fluxTicketUrl={fluxTicketUrl}
          onClose={() => setDetail(null)}
          onChanged={() => { load(); setDetail(null); }}
        />
      )}
    </div>
  );
}


// ------------------- CREATE MODAL -------------------
function CreateODTModal({ onClose, onCreated }) {
  const emptyItem = () => ({ product_id: "", quantity: 1 });
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [evasori, setEvasori] = useState([]);
  const [items, setItems] = useState([emptyItem()]);
  const [toWh, setToWh] = useState("");   // holds the mobile warehouse id → derives technician + destination
  const [note, setNote] = useState("");
  const [interventionDate, setInterventionDate] = useState("");
  const [evasoreUserId, setEvasoreUserId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses((r.data || []).filter((w) => w.type === "mobile"))).catch(() => {});
    api.get("/users/evasori").then((r) => {
      const list = r.data || [];
      setEvasori(list);
      const def = list.find((u) => u.is_default_odt_evasore);
      if (def) setEvasoreUserId(def.id);
    }).catch(() => {});
  }, []);

  const productOpts = useMemo(
    () => products.map((p) => ({
      value: p.id,
      label: `${p.sku} — ${p.name}`,
      sublabel: [
        p.is_serialized === false ? "Bulk (quantità)" : "Seriale",
        p.supplier_name ? `· ${p.supplier_name}` : null,
      ].filter(Boolean).join(" "),
      keywords: [p.sku, p.name, p.manufacturer_code, p.supplier_name].filter(Boolean),
    })),
    [products]
  );
  // The manual ODT chooses the *technician's van* — the technician name is
  // derived from the warehouse's owner_name (fallback to the warehouse name
  // if no owner is set on the record).
  const technicianOpts = useMemo(
    () => warehouses.map((w) => ({
      value: w.id,
      label: (w.owner_name && w.owner_name.trim()) || w.name,
      sublabel: `Furgone: ${w.name}`,
      keywords: [w.owner_name, w.name, w.description].filter(Boolean),
    })),
    [warehouses]
  );
  const selectedWh = warehouses.find((w) => w.id === toWh);

  const updateItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (i) => setItems((prev) => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!toWh || !selectedWh) return toast.error("Seleziona il tecnico (furgone) destinatario");
    if (!interventionDate) return toast.error("Inserisci la data di previsto intervento");
    const technicianName = (selectedWh.owner_name && selectedWh.owner_name.trim()) || selectedWh.name;
    const cleaned = items
      .map((it) => ({ product_id: (it.product_id || "").trim(), quantity: parseInt(it.quantity, 10) || 0 }))
      .filter((it) => it.product_id && it.quantity > 0);
    if (!cleaned.length) return toast.error("Aggiungi almeno un prodotto");

    setSaving(true);
    try {
      await api.post("/transfer-orders", {
        source: "manuale",
        technician_name: technicianName,
        to_warehouse_id: toWh,
        items: cleaned,
        note: note.trim() || null,
        intervention_date: interventionDate,
        evasore_user_id: evasoreUserId || null,
      });
      toast.success("ODT creato");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-neutral-300"
           onClick={(e) => e.stopPropagation()} data-testid="odt-create-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="font-display font-bold tracking-tight">Nuovo ODT manuale</div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-200"><XIcon size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="label-eyebrow text-neutral-700">Tecnico destinatario *</label>
            <SearchableSelect
              testid="odt-technician"
              value={toWh}
              options={technicianOpts}
              onChange={setToWh}
              placeholder="Seleziona il tecnico (furgone)…"
            />
            {selectedWh && (
              <div className="mt-1 text-xs text-neutral-500 flex items-center gap-1">
                <Truck size={12} /> Il carico sarà trasferito su <b className="ml-1 text-neutral-700">{selectedWh.name}</b>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-eyebrow text-neutral-700">Prodotti richiesti *</label>
              <button
                type="button"
                data-testid="odt-add-item-btn"
                onClick={addItem}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"
              >
                <Plus size={14} weight="bold" /> Aggiungi prodotto
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} data-testid={`odt-item-row-${idx}`}
                     className="flex items-start gap-2 p-2 border border-neutral-200 bg-neutral-50/50">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      testid={`odt-item-product-${idx}`}
                      value={it.product_id}
                      options={productOpts}
                      onChange={(v) => updateItem(idx, { product_id: v })}
                      placeholder="Cerca prodotto…"
                    />
                  </div>
                  <input
                    data-testid={`odt-item-qty-${idx}`}
                    type="number" min="1"
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    className="w-24 px-2 py-2 border border-neutral-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    className="p-2 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`odt-item-remove-${idx}`}
                  ><Trash size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow text-neutral-700">Data di previsto intervento *</label>
            <input
              data-testid="odt-intervention-date"
              type="date"
              value={interventionDate}
              onChange={(e) => setInterventionDate(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              required
            />
            <p className="text-xs text-neutral-500 mt-1">
              Giorno limite entro cui il materiale deve essere reso disponibile alla squadra.
            </p>
          </div>

          <div>
            <label className="label-eyebrow text-neutral-700 flex items-center gap-1.5">
              <Star size={12} weight="fill" className="text-amber-600" /> Evasore ODT
            </label>
            <select
              data-testid="odt-evasore"
              value={evasoreUserId}
              onChange={(e) => setEvasoreUserId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            >
              <option value="">— Nessuno (nessuna mail inviata) —</option>
              {evasori.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.is_default_odt_evasore ? "★ " : ""}{u.name} · {u.email}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              Riceverà una mail con il dettaglio dei materiali richiesti. Il valore di default è impostato tra gli Utenti; qui puoi sovrascriverlo solo per questo ODT.
            </p>
          </div>

          <div>
            <label className="label-eyebrow text-neutral-700">Note</label>
            <textarea
              data-testid="odt-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Riferimento ticket / motivazione"
              className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              data-testid="odt-submit"
              disabled={saving}
              onClick={submit}
              className="flex-1 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40"
            >{saving ? "Creo…" : "Crea ODT"}</button>
            <button onClick={onClose} className="px-4 py-2 border border-neutral-300 hover:bg-neutral-100">Annulla</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ------------------- DETAIL / FULFILL MODAL -------------------
function ODTDetailModal({ odt, canFulfill, fluxTicketUrl, onClose, onChanged }) {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const isOpen = odt.status === "aperto";

  const openFulfillForm = () => {
    // Reuse the standard "Nuovo Movimento" form: navigate to /movements with
    // pre-filled state. Movements.jsx catches the `odt` state and opens the
    // create form with type=transfer, from/to warehouses and items pre-loaded.
    // The warehouseman then picks the serials (or scans them) exactly like a
    // normal transfer and signs at the end. The bulk-create endpoint receives
    // `transfer_order_id` and closes this ODT server-side.
    navigate("/movements", {
      state: {
        odt: {
          id: odt.id,
          flux_odt_id: odt.flux_odt_id,
          from_warehouse_id: odt.from_warehouse_id,
          from_warehouse_name: odt.from_warehouse_name,
          to_warehouse_id: odt.to_warehouse_id,
          to_warehouse_name: odt.to_warehouse_name,
          technician_name: odt.technician_name,
          items: odt.items,
          note: odt.note,
        },
      },
    });
  };

  const cancel = async () => {
    if (!window.confirm("Annullare questo ODT? L'operazione non può essere ripristinata.")) return;
    setProcessing(true);
    try {
      await api.post(`/transfer-orders/${odt.id}/cancel`);
      toast.success("ODT annullato");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setProcessing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-neutral-300"
           onClick={(e) => e.stopPropagation()} data-testid="odt-detail-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <div>
            <div className="label-eyebrow text-[#002FA7]">
              ODT · {odt.source}
              {odt.flux_numero != null && (
                <span className="ml-2 font-bold text-[13px] text-[#002FA7] normal-case tracking-normal"
                      data-testid="odt-detail-numero">
                  #{odt.flux_numero}
                </span>
              )}
              {odt.odt_number && (
                <span className="ml-2 font-mono text-[12px] text-[#002FA7] normal-case tracking-normal"
                      data-testid="odt-detail-numero">
                  {odt.odt_number}
                </span>
              )}
            </div>
            <div className="font-mono text-xs text-neutral-500" data-testid="odt-detail-id">
              {odt.flux_odt_id || odt.id.slice(0, 8)}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-200"><XIcon size={20} /></button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] uppercase tracking-wider ${STATUS_STYLE[odt.status]}`}>
              {statusLabel(odt)}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] uppercase tracking-wider ${SOURCE_STYLE[odt.source]}`}>
              {odt.source}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow text-neutral-600">Tecnico</div>
              <div className="font-medium">{odt.technician_name}</div>
            </div>
            <div>
              <div className="label-eyebrow text-neutral-600">Rotta</div>
              <div className="text-xs">
                <span className="font-medium">{odt.from_warehouse_name}</span>
                {" → "}
                <span className="font-medium">{odt.to_warehouse_name}</span>
              </div>
            </div>
            {(() => {
              const t = readTicket(odt);
              const renderBadge = (code, size = "sm") => {
                const href = fluxTicketHref(code, fluxTicketUrl);
                const sz = size === "sm" ? "text-sm px-2 py-1" : "text-xs px-2 py-0.5";
                if (href) {
                  return (
                    <a
                      key={code}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Apri ${code} su Flux`}
                      className={`inline-flex items-center gap-1 ${sz} border border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-mono hover:bg-[#002FA7]/10 transition-colors`}
                    >
                      {code}
                      <ArrowSquareOut size={12} weight="bold" />
                    </a>
                  );
                }
                return (
                  <span key={code} className={`inline-flex items-center ${sz} border border-neutral-300 bg-neutral-50 font-mono`}>
                    {code}
                  </span>
                );
              };
              return t.primary ? (
                <div>
                  <div className="label-eyebrow text-neutral-600">Ticket{t.extraCount > 0 ? " (multipli)" : ""}</div>
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="odt-detail-ticket">
                    {renderBadge(t.primary, "sm")}
                    {t.all.slice(1).map((c) => renderBadge(c, "xs"))}
                  </div>
                </div>
              ) : null;
            })()}
            {odt.intervention_date && (
              <div>
                <div className="label-eyebrow text-neutral-600">Data intervento programmato</div>
                <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] text-sm font-medium"
                     data-testid="odt-detail-intervention-date">
                  <CalendarBlank size={14} weight="bold" /> {fmtInterventionDate(odt.intervention_date)}
                </div>
              </div>
            )}
          </div>

          {odt.note && (
            <div>
              <div className="label-eyebrow text-neutral-600">Note</div>
              <div className="whitespace-pre-wrap text-xs bg-neutral-50 border border-neutral-200 p-2">{odt.note}</div>
            </div>
          )}

          <div>
            <div className="label-eyebrow text-neutral-600 mb-1">Items</div>
            <div className="space-y-2">
              {odt.items.map((it) => (
                <div key={it.product_id} className="border border-neutral-200"
                     data-testid={`odt-item-detail-${it.product_id}`}>
                  <div className="p-2 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-neutral-500">{it.sku}</span>
                        <span className="text-neutral-300">·</span>
                        <span
                          className={`text-xs ${it.supplier_name ? "text-neutral-700" : "italic text-neutral-400"}`}
                          data-testid={`odt-item-supplier-${it.product_id}`}
                          title={it.supplier_name ? `Fornitore: ${it.supplier_name}` : "Fornitore non specificato"}
                        >
                          {it.supplier_name || "Fornitore non specificato"}
                        </span>
                      </div>
                      <div className="font-medium">{it.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono">{it.quantity} pz</div>
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                        {it.is_serialized ? "seriale" : "bulk"}
                      </div>
                    </div>
                  </div>
                  {!isOpen && it.is_serialized && (
                    <div className="p-2 text-xs">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-600 mb-1">Seriali assegnati</div>
                      {((odt.serials_assigned || []).find((a) => a.product_id === it.product_id)?.serial_numbers || []).map((sn) => (
                        <span key={sn} className="inline-block mr-1 mb-1 px-1.5 py-0.5 border border-neutral-300 bg-neutral-50 font-mono">{sn}</span>
                      )) || <span className="text-neutral-400">—</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-neutral-500 border-t border-neutral-200 pt-2 space-y-0.5">
            <div>Creato il {fmtDate(odt.created_at)} da {odt.created_by_name || odt.created_by}</div>
            {odt.evaso_at && <div>Evaso il {fmtDate(odt.evaso_at)} da {odt.evaso_by_name || odt.evaso_by}</div>}
            {odt.cancelled_at && <div>Annullato il {fmtDate(odt.cancelled_at)} da {odt.cancelled_by_name || odt.cancelled_by}</div>}
          </div>

          {isOpen && canFulfill && (
            <div className="flex gap-2 pt-2 border-t border-neutral-200">
              <button
                data-testid="odt-fulfill-btn"
                disabled={processing}
                onClick={openFulfillForm}
                title="Apre il modulo di trasferimento manuale precompilato con i dati dell'ODT"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
              >
                <CheckCircle size={16} weight="bold" /> Evadi ODT
              </button>
              <button
                data-testid="odt-cancel-btn"
                disabled={processing}
                onClick={cancel}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-40"
              >
                <XCircle size={16} weight="bold" /> Annulla ODT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
